<template>
  <div 
    v-loading="loading"
    :class="b()"
  >
    <video
      ref="videoRef"
      :class="b('video')"
      v-bind="videoOptions"
      :controls="showControls"
    >
      <source 
        :src="transformUrl"
        type="video/mp4"
      >
      <source 
        :src="url" 
        type="video/webm" 
      >
      <p>{{ notSupportTip }}</p>
    </video>
    <div 
      v-if="videoName"
      :class="[b('video-info'), 'text-ellipsis-l2']"
    >
      <span>{{ videoName }}</span>
      <span 
        v-if="!isAnonymous"
        :class="b('video-author')"
      >
        {{ author.name }}
      </span>
    </div>
  </div>
</template>

<script>
import {
  defineComponent,
  computed,
  ref,
  onMounted,
  onBeforeUnmount
} from 'vue-demi';
import theme from '@zk/theme/lib/variables.scss';
import { waitGetAny } from '@zk/framework-vue';
import { useVuex } from 'common-module/src/util/useVuex';

const DEFAULT_VIDEO_OPTIONS = {
  controls: true,
  // eslint-disable-next-line spellcheck/spell-checker
  controlslist: 'nodownload',
  x5VideoPlayerType: 'h5-page'
};

export const videoProps = {
  // 视频地址
  url: {
    type: String,
    default: ''
  },
  // 打开自动播放
  autoPlay: {
    type: Boolean,
    default: true
  },
  // 原生的视频属性
  videoAttribute: {
    type: Object,
    default: () => ({})
  },
  notSupportTip: {
    type: String,
    default: '暂不支持该视频播放'
  },
  // 开始播放触发
  onPlay: {
    type: Function,
    default: () => {}
  },
  // 暂停播放触发
  onPause: {
    type: Function,
    default: () => {}
  },
  // 播放结束触发
  onEnded: {
    type: Function,
    default: () => {}
  },
  videoName: {
    type: String,
    default: ''
  },
  videoAuthor: {
    type: { String, Object },
    default: null
  }
};

export default defineComponent({
  name: 'VideoPlayer',
  bemNS: theme.bemNS,
  props: videoProps,
  setup: (props, { emit }) => {
    const videoRef = ref();
    const showControls = ref(false);
    const transformUrl = ref();
    const store = useVuex();
    const isAnonymous = computed(() => store.getters['user/isAnonymous']);
    const loading = ref(true);

    const videoOptions = computed(() => ({
      ...DEFAULT_VIDEO_OPTIONS,
      ...props.videoAttribute
    }));

    const getIsFullScreen = () => {
      return (
        !!document.fullscreenElement ||
        // eslint-disable-next-line spellcheck/spell-checker
        document.mozFullScreen ||
        document.webkitIsFullScreen
      );
    };

    const getCurrentState = () => {
      const { currentTime, ended } = videoRef.value;
      return {
        currentTime,
        ended,
        isFullScreen: getIsFullScreen()
      };
    };

    const handlePlay = () => {
      const state = getCurrentState();
      props.onPlay(state);
      emit('play', state);
    };

    const handlePause = () => {
      const state = getCurrentState();
      props.onPause(state);
      emit('pause', state);
    };

    const handleEnded = () => {
      const state = getCurrentState();
      props.onEnded(state);
      emit('ended', state);
    };

    // eslint-disable-next-line spellcheck/spell-checker
    const handleLoadedmetadata = async () => {
      // chrome 无法播放 Infinity 时长的视频
      const videoElement = videoRef.value;
      if (videoElement.duration === Infinity) {
        videoElement.currentTime = Number.MAX_SAFE_INTEGER;
        await waitGetAny(() => {
          return videoElement.duration === Infinity ? null : videoElement.duration;
        }, { timeout: 10000, interval: 20 });
        videoElement.currentTime = 0;
      }
      showControls.value = true; 
      loading.value = false;
      if (props.autoPlay){
        videoRef.value.play();
      }
    };

    const addEventListener = () => {
      videoRef.value.addEventListener('play', handlePlay);
      videoRef.value.addEventListener('pause', handlePause);
      videoRef.value.addEventListener('ended', handleEnded);
      // eslint-disable-next-line spellcheck/spell-checker
      videoRef.value.addEventListener('loadedmetadata', handleLoadedmetadata);
    };

    const removeEventListener = () => {
      videoRef.value.removeEventListener('play', handlePlay);
      videoRef.value.removeEventListener('pause', handlePause);
      videoRef.value.removeEventListener('ended', handleEnded);
      // eslint-disable-next-line spellcheck/spell-checker
      videoRef.value.removeEventListener('loadedmetadata', handleLoadedmetadata);
    };

    onMounted(() => {
      if (!props.url) {
        return;
      }
      const [url, query] = props.url.split('?');
      if (url.endsWith('.webm')) {
        transformUrl.value = `${url.replace(/\.webm$/, '.mp4')}?${query || ''}`;
      }
      addEventListener();
      if (videoRef.value.duration !== Infinity) {
        showControls.value = true;
        loading.value = false;
      }
    });

    onBeforeUnmount(() => {
      removeEventListener();
    });

    const isValidJSON = (str) => {
      if (!str) {
        return false;
      }
      try {
        JSON.parse(str);
      } catch (e) {
        return false;
      }
      return true;
    };

    const author = computed(() => {
      if (typeof props.videoAuthor === 'object') {
        return props.videoAuthor;
      }
      if (typeof props.videoAuthor === 'string') {
        return isValidJSON(props.videoAuthor) ? JSON.parse(props.videoAuthor) : { name: props.videoAuthor };
      }
      return {};
    });

    return {
      loading,
      videoRef,
      videoOptions,
      getCurrentState,
      showControls,
      transformUrl,
      isAnonymous,
      author
    };
  }
});
</script>

<style scoped lang="scss">
@include b(video-player) {
  width: 100%;
  height: 100%;
  @include e(video) {
    display: block;
    width: 100%;
    height: 100%;
  }
  @include e(prohibit-text){
    color: var(--purpleGray-5);
  }
  @include e(video-info) {
    color: #fff;
    width: 100%;
    height: 50px;
    line-height: 50px;
    margin-bottom: -50px;
  }
  @include e(video-author) {
    margin-left: 8px;
  }
}
</style>
